import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import keycloak from '../config/keycloak';
import type { KeycloakProfile } from 'keycloak-js';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | undefined;
  profile: KeycloakProfile | undefined;
  login: () => void;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [profile, setProfile] = useState<KeycloakProfile | undefined>(undefined);
  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      initAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once; didInit guards StrictMode double-invoke
  }, []);

  const initAuth = async () => {
    try {
      console.log('Initializing Keycloak...');
      
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      
      const code = urlParams.get('code') || hashParams.get('code');
      const state = urlParams.get('state') || hashParams.get('state');
      const sessionState = urlParams.get('session_state') || hashParams.get('session_state');
      
      if (code) {
        console.log('Auth code detected in URL, processing authentication...');
      }
      
      const authenticated = await keycloak.init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        pkceMethod: 'S256',
        redirectUri: window.location.origin + window.location.pathname,
        responseMode: 'fragment',
      });

      console.log('Keycloak initialized, authenticated:', authenticated);
      
      if (authenticated && (code || state || sessionState)) {
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
      
      setIsAuthenticated(authenticated);

      if (authenticated && keycloak.token) {
        setToken(keycloak.token);
        
        try {
          const userProfile = await keycloak.loadUserProfile();
          setProfile(userProfile);
          console.log('User profile loaded:', userProfile);
        } catch (error) {
          console.error('Failed to load user profile:', error);
        }

        setupTokenRefresh();
      }
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const setupTokenRefresh = () => {
    let refreshTimer: NodeJS.Timeout;

    const scheduleRefresh = () => {
      const refreshTime = (keycloak.tokenParsed?.exp ?? 0) - Math.floor(Date.now() / 1000) - 30;
      
      if (refreshTime > 0) {
        refreshTimer = setTimeout(async () => {
          try {
            const refreshed = await keycloak.updateToken(30);
            if (refreshed && keycloak.token) {
              setToken(keycloak.token);
              console.log('Token refreshed successfully');
              scheduleRefresh();
            }
          } catch (error) {
            console.error('Failed to refresh token:', error);
            logout();
          }
        }, refreshTime * 1000);
      }
    };

    scheduleRefresh();

    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  };

  const login = () => {
    if (keycloak && keycloak.login) {
      keycloak.login({
        redirectUri: window.location.origin,
        responseMode: 'fragment',
      });
    } else {
      const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL;
      const realm = process.env.REACT_APP_KEYCLOAK_REALM;
      const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;
      
      if (!keycloakUrl || !realm || !clientId) {
        console.error('Keycloak configuration missing');
        return;
      }
      
      const redirectUri = encodeURIComponent(window.location.origin);
      const loginUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `scope=openid%20profile%20email&` +
        `response_mode=fragment`;
      
      window.location.href = loginUrl;
    }
  };

  const logout = () => {
    if (keycloak && keycloak.logout) {
      keycloak.logout({
        redirectUri: window.location.origin,
      });
    } else {
      const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL;
      const realm = process.env.REACT_APP_KEYCLOAK_REALM;
      
      if (!keycloakUrl || !realm) {
        console.error('Keycloak configuration missing');
        return;
      }
      
      const redirectUri = encodeURIComponent(window.location.origin);
      const logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout?` +
        `redirect_uri=${redirectUri}`;
      
      window.location.href = logoutUrl;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshed = await keycloak.updateToken(5);
      if (refreshed && keycloak.token) {
        setToken(keycloak.token);
      }
      return refreshed;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        token,
        profile,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};